import { Component, lazy, MouseEvent, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import theme from "./theme";
import HeaderBar from "./Shell/HeaderBar";
import {
	Box,
	CircularProgress,
	CssBaseline,
	ThemeProvider,
} from "@mui/material";
import SideNavigation from "./Shell/SideNavigation";
import SuspenseContainer from "./Common/Widgets/SuspenseContainer";
import {
	AttestationNetworks,
	CurrentNetwork,
	IcpSignedBlockNetworks,
	MetricsNetworks,
	NymSignedBlockNetworks,
	RpcNetworks,
	SignedBlockNetworks,
	SubscriptionNetworks,
} from "./Common/Models/CustomTypes";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import AboutUs from "./AboutUs";

// https://reactjs.org/docs/code-splitting.html
// celo
const AsyncNotFound = lazy(() => import("./NotFound"));
const AsyncSignedBlocks = lazy(() => import("./Celo/SignedBlocks"));
const AsyncAttestationMap = lazy(() => import("./Celo/AttestationMap"));
const AsyncMetricsMap = lazy(() => import("./Celo/Metrics"));
const AsyncSubscriptions = withAuthenticationRequired(
	lazy(() => import("./Celo/Subscriptions")),
);
const AsyncRpc = lazy(() => import("./Celo/Rpc"));
// CometBFT
const AsyncCometBftSignedBlocks = lazy(() => import("./CometBFT/SignedBlocks"));
const AsyncCometBftMetricsMap = lazy(() => import("./CometBFT/Metrics"));
const AsyncCometBftSubscriptions = withAuthenticationRequired(
	lazy(() => import("./CometBFT/Subscriptions")),
);

const signedBlocks: string[] = SignedBlockNetworks;
const attestationMaps: string[] = AttestationNetworks;
const rpcMaps: string[] = RpcNetworks;
const metricsMaps: string[] = MetricsNetworks;
const subscriptionsMaps: string[] = SubscriptionNetworks;
const nymSignedBlocks: string[] = NymSignedBlockNetworks;
const icpSignedBlocks: string[] = IcpSignedBlockNetworks;

// ICP
const AsyncIcpSignedBlocks = lazy(() => import("./ICP/Measurements"));
const AsyncIcpMetricsMap = lazy(() => import("./ICP/Metrics"));
const AsyncIcpSubscriptions = withAuthenticationRequired(
	lazy(() => import("./ICP/Subscriptions")),
);

type AppProp = { appName: string };
type AppState = { open: boolean };

class App extends Component<AppProp, AppState> {
	constructor(props: AppProp) {
		super(props);
		this.state = { open: true };
		localStorage.getItem(CurrentNetwork);
	}

	onMenuClick = (event: MouseEvent): void => {
		event.stopPropagation();
		event.preventDefault();
		if (this.state.open) {
			this.handleDrawerClose();
		} else {
			this.handleDrawerOpen();
		}
	};

	handleDrawerOpen = (): void => {
		this.setState({
			open: true,
		});
	};

	handleDrawerClose = (): void => {
		this.setState({
			open: false,
		});
	};

	render(): JSX.Element {
		return (
			<ThemeProvider theme={theme}>
				<Box sx={{ display: "flex" }}>
					<CssBaseline />
					<Suspense
						fallback={
							<Box
								display="flex"
								sx={{ height: "100vh", width: "100%" }}
								alignItems="center"
								justifyContent="center"
							>
								<CircularProgress color="primary" />
							</Box>
						}
					>
						<HeaderBar onMenuClick={this.onMenuClick} />
						<SideNavigation open={this.state.open} />
						<Box
							component="main"
							sx={{ flexGrow: 1 }}
							paddingTop="60px"
						>
							<Routes>
								<Route
									path="/"
									element={
										<SuspenseContainer>
											<AsyncSignedBlocks
												network={
													localStorage.getItem(
														CurrentNetwork,
													) ?? "Mainnet"
												}
												lookback={100}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									path="/metrics"
									element={
										<SuspenseContainer>
											<AsyncMetricsMap
												key={`celo-mainnet-default-path-metrics-page-${metricsMaps[0]}`}
												network={metricsMaps[0]}
											/>
										</SuspenseContainer>
									}
								/>
								{/* <Redirect from="/" to="/block-map" /> */}
								{attestationMaps.map((am, i) => (
									<Route
										key={i}
										path={
											"/" +
											(am.toLowerCase() === "mainnet"
												? ""
												: am.toLowerCase() + "-") +
											"attestations"
										}
										element={
											<SuspenseContainer>
												<AsyncAttestationMap
													key={i}
													network={am}
													lookback={50}
												/>
											</SuspenseContainer>
										}
									/>
								))}
								<Route
									key={`celo-mainnet-route-${signedBlocks[0]}`}
									path={"/celo/block-map"}
									element={
										<SuspenseContainer>
											<AsyncSignedBlocks
												key={`celo-mainnet-page-${signedBlocks[0]}`}
												network={signedBlocks[0]}
												lookback={100}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`celo-mainnet-rpc-route-${rpcMaps[0]}`}
									path={"/celo/rpc"}
									element={
										<SuspenseContainer>
											<AsyncRpc
												key={`celo-mainnet-rpc-page-${rpcMaps[0]}`}
												network={rpcMaps[0]}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`celo-mainnet-attestations-route-${attestationMaps[0]}`}
									path={"/celo/attestations"}
									element={
										<SuspenseContainer>
											<AsyncAttestationMap
												key={`celo-mainnet-attestations-page-${attestationMaps[0]}`}
												network={attestationMaps[0]}
												lookback={50}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`celo-mainnet-metrics-route-${metricsMaps[0]}`}
									path={"/celo/metrics"}
									element={
										<SuspenseContainer>
											<AsyncMetricsMap
												key={`celo-mainnet-metrics-page-${metricsMaps[0]}`}
												network={metricsMaps[0]}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`celo-mainnet-subscriptions-route-${subscriptionsMaps[0]}`}
									path={"/celo/subscriptions"}
									element={
										<SuspenseContainer>
											<AsyncSubscriptions
												key={`celo-mainnet-subscriptions-page-${subscriptionsMaps[0]}`}
												network={subscriptionsMaps[0]}
											/>
										</SuspenseContainer>
									}
								/>
								{/* NYM */}
								<Route
									key={`nym-block-map-mainnet`}
									path={"/nym/block-map"}
									element={
										<SuspenseContainer>
											<AsyncCometBftSignedBlocks
												key={`nym-block-map-mainnet-block`}
												network={nymSignedBlocks[0]}
												lookback={100}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`nym-metrics-route`}
									path={"/nym/metrics"}
									element={
										<SuspenseContainer>
											<AsyncCometBftMetricsMap
												key={`nym-metrics-page-${nymSignedBlocks[0]}`}
												network={nymSignedBlocks[0]}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`nym-subscriptions-route-${nymSignedBlocks[0]}`}
									path={"/nym/subscriptions"}
									element={
										<SuspenseContainer>
											<AsyncCometBftSubscriptions
												key={`nym-subscriptions-page-${nymSignedBlocks[0]}`}
												network={nymSignedBlocks[0]}
											/>
										</SuspenseContainer>
									}
								/>
								{/* ICP */}
								<Route
									key={`icp-block-map-mainnet`}
									path={"/icp/measurements"}
									element={
										<SuspenseContainer>
											<AsyncIcpSignedBlocks
												key={`icp-block-map-mainnet-block`}
												network={icpSignedBlocks[0]}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`icp-metrics-route`}
									path={"/icp/metrics"}
									element={
										<SuspenseContainer>
											<AsyncIcpMetricsMap
												key={`icp-metrics-page-${icpSignedBlocks[0]}`}
												network={icpSignedBlocks[0]}
											/>
										</SuspenseContainer>
									}
								/>
								<Route
									key={`icp-subscriptions-route-${icpSignedBlocks[0]}`}
									path={"/icp/subscriptions"}
									element={
										<SuspenseContainer>
											<AsyncIcpSubscriptions
												key={`icp-subscriptions-page-${icpSignedBlocks[0]}`}
												network={icpSignedBlocks[0]}
											/>
										</SuspenseContainer>
									}
								/>

								<Route path="/aboutus" element={<AboutUs />} />
								<Route path="*" element={<AsyncNotFound />} />
							</Routes>
						</Box>
					</Suspense>
				</Box>
			</ThemeProvider>
		);
	}
}

export default App;
